import { useState } from 'react';
import type { ImportantDate } from '@gl/shared';

import { useDeleteImportantDate, useSaveImportantDate } from '../api/hooks.js';
import { calendarParts, relativeDays } from '../util/format.js';
import { Modal } from './Modal.js';

interface Props {
  dates: ImportantDate[];
  canEdit: boolean;
}

interface Draft {
  id?: string;
  date: string;
  time: string;
  text: string;
}

const emptyDraft = (): Draft => ({ date: '', time: '', text: '' });

/**
 * The date as a calendar tile.
 *
 * These entries exist to answer "when", so the day is the loudest thing on the
 * row rather than a line of small print under the description.
 */
function DateTile({ date, time }: { date: string; time: string | null }) {
  const parts = calendarParts(date, time);
  if (!parts) return null;

  return (
    // Not hidden from a screen reader: the line beside it deliberately leaves
    // the month and day out, so this is where they are said.
    <div
      className="flex w-14 shrink-0 flex-col items-center border border-field-700 bg-field-950
        leading-none"
    >
      <span className="w-full bg-brass-400 py-0.5 text-center font-display text-[0.625rem] font-bold uppercase tracking-bug text-field-950">
        {parts.month}
      </span>
      <span className="py-1 font-display text-3xl font-bold leading-none text-field-50">
        {parts.day}
      </span>
    </div>
  );
}

/**
 * "Saturday · 7:30 PM · in 12 days" — the tile has already given the month and
 * the day. The year only appears when it is not this one, which is the only
 * time anybody wonders.
 */
function whenLabel(date: string, time: string | null): string {
  const parts = calendarParts(date, time);
  if (!parts) return date;

  const year = parts.year === new Date().getFullYear() ? null : String(parts.year);
  return [parts.weekday, year, parts.time, relativeDays(date)].filter(Boolean).join(' · ');
}

/**
 * Important dates list with inline add/edit modals, per the docs' requirement
 * that these be editable right on the home page.
 */
export function ImportantDatesCard({ dates, canEdit }: Props) {
  const [draft, setDraft] = useState<Draft | null>(null);
  const save = useSaveImportantDate();
  const remove = useDeleteImportantDate();

  const submit = () => {
    if (!draft) return;
    save.mutate(
      {
        id: draft.id,
        input: { date: draft.date, time: draft.time || null, text: draft.text.trim() },
      },
      { onSuccess: () => setDraft(null) },
    );
  };

  return (
    <div className="card">
      {dates.length === 0 ? (
        <p className="text-sm text-field-400">Nothing on the calendar.</p>
      ) : (
        <ul className="space-y-2">
          {dates.map((date) => (
            <li
              key={date.id}
              className="flex items-start gap-3 border border-field-800 bg-field-950 p-2"
            >
              <DateTile date={date.date} time={date.time} />

              <div className="min-w-0 flex-1">
                <p className="font-display text-xs font-semibold uppercase tracking-bug text-brass-400">
                  {whenLabel(date.date, date.time)}
                </p>
                <p className="mt-1 font-display text-base font-semibold uppercase tracking-tight text-field-50">
                  {date.text}
                </p>
              </div>
              {canEdit ? (
                <div className="flex shrink-0 flex-col gap-1">
                  <button
                    type="button"
                    className="btn-ghost px-2 py-1 text-xs"
                    onClick={() =>
                      setDraft({
                        id: date.id,
                        date: date.date,
                        time: date.time ?? '',
                        text: date.text,
                      })
                    }
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className="btn-ghost px-2 py-1 text-xs text-signal hover:bg-signal/20"
                    onClick={() => {
                      if (confirm(`Delete "${date.text}"?`)) remove.mutate(date.id);
                    }}
                  >
                    Delete
                  </button>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {canEdit ? (
        <button
          type="button"
          className="btn-secondary mt-4 w-full"
          onClick={() => setDraft(emptyDraft())}
        >
          Add a date
        </button>
      ) : null}

      <Modal
        open={draft !== null}
        title={draft?.id ? 'Edit date' : 'Add a date'}
        onClose={() => setDraft(null)}
        footer={
          <>
            <button type="button" className="btn-secondary" onClick={() => setDraft(null)}>
              Cancel
            </button>
            <button
              type="button"
              className="btn-primary"
              disabled={!draft?.date || !draft.text.trim() || save.isPending}
              onClick={submit}
            >
              {save.isPending ? 'Saving…' : 'Save'}
            </button>
          </>
        }
      >
        {draft ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label" htmlFor="date-date">
                  Date
                </label>
                <input
                  id="date-date"
                  type="date"
                  className="input"
                  value={draft.date}
                  onChange={(event) => setDraft({ ...draft, date: event.target.value })}
                />
              </div>
              <div>
                <label className="label" htmlFor="date-time">
                  Time <span className="text-field-500">(optional)</span>
                </label>
                <input
                  id="date-time"
                  type="time"
                  className="input"
                  value={draft.time}
                  onChange={(event) => setDraft({ ...draft, time: event.target.value })}
                />
              </div>
            </div>
            <div>
              <label className="label" htmlFor="date-text">
                What is happening
              </label>
              <input
                id="date-text"
                className="input"
                placeholder="Rookie draft"
                value={draft.text}
                onChange={(event) => setDraft({ ...draft, text: event.target.value })}
              />
            </div>
            {save.error ? (
              <p className="text-sm text-signal">{(save.error as Error).message}</p>
            ) : null}
          </div>
        ) : null}
      </Modal>
    </div>
  );
}
